import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {IconsComponent} from '../../../../shared/components/icons/icons.component';
import {LinkComponent} from '../../../../shared/components/link/link.component';
import {ResultsStore} from '../../../../shared/store/results.store';
import {ResultTableService} from '../../services/result-table.service';
import { AlternateFitmentsModal } from '../../../lookup/components/lookup-by-vehicle/alternate-fitments-modal/alternate-fitments-modal';
import {IAlternateVehicleFitment} from '../../../../types/IAlternateVehicleFitment';
import {LookupStore} from '../../../../shared/store/lookup.store';
import {TourService} from '../../../../shared/tour/tour.service';
import {getSourceAvailabilityByType} from '../../../../shared/utils/availability.utils';
import {ButtonComponent} from '../../../../shared/components/button/button.component';
import {PostHogService} from '../../../../shared/services/posthog.service';
import {AccessService} from '../../../../shared/services/access.service';
import {StorageService} from '../../../../shared/services/storage.service';

@Component({
  selector: 'app-results-header',
  standalone: true,
  imports: [IconsComponent, LinkComponent, AlternateFitmentsModal, ButtonComponent],
  templateUrl: './results-header.component.html',
  styleUrl: './results-header.component.css'
})
export class ResultsHeaderComponent implements OnInit {
  access = inject(AccessService);
  resultsStore = inject(ResultsStore);
  lookupStore = inject(LookupStore);
  resultTableService = inject(ResultTableService);
  tourService = inject(TourService);
  posthogService = inject(PostHogService);
  private storage = inject(StorageService);

  alternateFitmentsModalOpen = signal(false);

  readonly settingsVisible = computed(() => this.access.has('edit_settings'));
  readonly settingsDisabled = computed(() => this.access.cannot('edit_settings'));

  ngOnInit() {
    this.registerTour();
    this.registerFeedbackNudge();
    this.registerTourNudge();
    this.maybeShowNudges();
  }

  getSearch = computed(() => {
    const params = this.resultsStore.search_params();
    return params?.rawsize || params?.part_number || params?.vin || '';
  });

  onAlternateSizeSearch(alternate: IAlternateVehicleFitment) {
    this.lookupStore.setRawsize(alternate.tire_size);
    this.alternateFitmentsModalOpen.set(false);

    const rawsize = this.lookupStore.vehicle.selected_rawsize();
    if (rawsize) {
      this.resultsStore.search({rawsize})
    }
  }

  private registerTour() {
    this.tourService.register('results', [
      {attachTo: {element: '#results-toggle-search-filters'}, title: 'Filters', text: 'Toggle the filters sidebar to free up screen space'},
      {attachTo: {element: '#results-change-search-details'}, title: 'Search Details', text: 'Update your year, make, model, or size selection'},
      {attachTo: {element: '#results-alternate-fitments'}, title: 'Alternate Fitments', text: 'View tires in similar sizes that also fit your vehicle', canShow: () => !!this.lookupStore.vehicle.selected_fitment()},
      {
        attachTo: {element: '#results-settings-cog', on: 'left-end'},
        title: 'Settings',
        text: 'Adjust your preferences and application settings',
        canShow: () => this.access.has('edit_settings'),
      },
      {
        attachTo: {element: '.item-image:first-of-type', on: "right-end"},
        title: 'Item Details',
        text: 'Click to view specs, images, and additional product information',
        onNext: () => { this.tourService.next(); this.resultTableService.openDetailsModal(this.resultTableService.allRows()[0]); },
      },
      {
        attachTo: {element: '.item-edit:first-of-type'},
        title: 'Edit Item',
        text: 'Open item details in your management system to edit inventory information',
        beforeShowPromise: () => new Promise(resolve => { setTimeout(() => { resolve(); }, 50); }),
        onPrev: () => { this.resultTableService.closeModal(); this.tourService.prev(); },
        canShow: () => this.access.has('edit_item'),
      },
      {
        attachTo: {element: '.item-history'},
        title: 'Item History',
        text: 'View past sales and order history for this items',
        beforeShowPromise: () => new Promise(resolve => {
          if (!this.resultTableService.activeModal()) {
            this.resultTableService.openDetailsModal(this.resultTableService.allRows()[0]);
            return setTimeout(() => resolve(), 50)
          }
          return resolve();
        }),
        onNext: () => { this.tourService.next(); this.resultTableService.closeModal(); },
        canShow: () => this.access.has('view_item_history'),
      },
      {
        attachTo: {element: '.item-rebate:first-of-type'},
        title: 'Rebates',
        text: 'This tire has available rebates — click to view details',
        canShow: () => this.resultsStore.results()?.some((item) => item.rebates?.length),
      },
      {
        attachTo: {element: '.item-spiff:first-of-type'},
        title: 'Spiff Codes',
        text: 'This tire includes a dealer spiff — click to view details',
        canShow: () => this.resultsStore.results()?.some((item) => item.spiff?.amount) && this.access.has('view_spiff_codes') && this.access.can('view_spiff_codes'),
      },
      {attachTo: {element: '.item-local:first-of-type'}, title: 'Local Inventory', text: 'Click to see how many are available at your location', canShow: () => this.resultsStore.results()?.some((item) => getSourceAvailabilityByType(item.availability, 'local'))},
      {attachTo: {element: '.item-transfer:first-of-type'}, title: 'Transfer Stock', text: 'Click to see quantities available for transfer from your locations', canShow: () => this.resultsStore.results()?.some((item) => getSourceAvailabilityByType(item.availability, 'transfer'))},
      {attachTo: {element: '.item-distributor:first-of-type'}, title: 'Distributor Stock', text: 'Click to see quantities available from your distributors', canShow: () => this.resultsStore.results()?.some((item) => getSourceAvailabilityByType(item.availability, 'distributor'))},
      {attachTo: {element: '.item-add-to-compare:first-of-type'}, title: 'Compare Items', text: 'Add this item to the comparison to evaluate it side by side'},
      {
        attachTo: {element: '.item-add-to-quote:first-of-type'},
        title: 'Add to Quote',
        text: 'Add this item to the current customer quote',
        onNext: () => { this.tourService.next(); this.resultTableService.openSupplierModal(this.resultTableService.allRows()[0], 'quote') },
      },
      {
        attachTo: {element: '#supplier-selection-quick-add'},
        title: 'Quick Add',
        text: 'Click the Quick Add button to instantly add the item to the quote without selecting vendors, creating purchase orders, or add-on items',
        beforeShowPromise: () => new Promise(resolve => { setTimeout(() => { resolve(); }, 50); }),
        onPrev: () => { this.resultTableService.closeModal(); this.tourService.prev(); },
      },
      {
        attachTo: {element: '#supplier-selection-add-to-quote'},
        title: 'Add to quote',
        text: 'Click the Add to Quote button to skip the comparison options and quickly jump to the next step, choose your add-ons, and select if a PO should be created/submitted',
        beforeShowPromise: () => new Promise(resolve => {
          if (!this.resultTableService.activeModal()) this.tourService.stop();
          resolve();
        }),
        onNext: () => { this.tourService.next(); this.resultTableService.closeModal(); },
      },
      {
        title: 'You\'re All Set!',
        text: 'That covers the results page — you\'re ready to start looking up tires',
        arrow: false,
        onPrev: () => { this.resultTableService.openSupplierModal(this.resultTableService.allRows()[0], 'quote'), setTimeout(() => this.tourService.prev(), 50) },
      },
    ]);
  }

  doFeedback() {
    this.posthogService.posthog.capture('provide_feedback');
  }

  private registerTourNudge() {
    this.tourService.register('tour-nudge', [
      {
        attachTo: { element: '#results-tour' },
        title: 'Take a quick tour',
        text: 'New here? Click this button for a quick walkthrough of the results page — we\'ll show you where everything lives so you can get the most out of it.',
        buttons: [{ text: 'Got it', action: () => this.tourService.stop(), variant: 'primary' }],
      },
    ], { hasBackdrop: false });
  }

  private registerFeedbackNudge() {
    this.tourService.register('feedback-nudge', [
      {
        attachTo: { element: '#results-feedback-button', on: 'bottom-end' },
        title: 'We want to hear from you!',
        text: 'You\'re using an early beta — click the Feedback button to share what\'s working, what\'s not, or anything else on your mind!',
        buttons: [{ text: 'Got it', action: () => this.tourService.stop(), variant: 'primary' }],
      },
    ], { hasBackdrop: false });
  }

  private maybeShowNudges() {
    const tourShownKey = 'tour-nudge-shown';
    const feedbackShownKey = 'feedback-nudge-last-shown';

    if (!this.storage.get(tourShownKey)) {
      this.storage.set(tourShownKey, String(Date.now()));
      setTimeout(() => this.tourService.start('tour-nudge'), 1500);
      return;
    }

    const lastFeedback = this.storage.get(feedbackShownKey);
    const now = Date.now();
    const feedbackIsStale = !lastFeedback || now - parseInt(lastFeedback, 10) > 7 * 24 * 60 * 60 * 1000;

    if (feedbackIsStale) {
      this.storage.set(feedbackShownKey, String(now));
      setTimeout(() => this.tourService.start('feedback-nudge'), 1500);
    }
  }
}
